import {
  DashboardRefreshFailureReasons,
  DashboardRefreshResults,
  type DashboardRefreshTriggerResultType,
} from "@validereinc/domain";
import {
  datetimeFormatter,
  formatSecondsAsRoundedUpMinutes,
} from "@validereinc/utilities";

const MILLISECONDS_PER_MINUTE = 60 * 1000;

export const DASHBOARD_REFRESH_COOLDOWN_MS = 20 * MILLISECONDS_PER_MINUTE;

export const getDashboardRefreshToastText = (
  result: DashboardRefreshTriggerResultType
): string => {
  if (result.result === DashboardRefreshResults.SUCCESS) {
    return "Refresh triggered. This can take a few minutes to finish.";
  }

  if (result.reason === DashboardRefreshFailureReasons.COOLDOWN_ACTIVE) {
    return result.retryAfterSeconds
      ? `This dashboard was refreshed recently. Try again in ${formatSecondsAsRoundedUpMinutes(result.retryAfterSeconds)}.`
      : "This dashboard was refreshed recently. Try again later.";
  }

  return "Something went wrong triggering the refresh. Try again in a moment.";
};

export const getDashboardLastRefreshedAtText = (
  lastManuallyRefreshedAt: string | null | undefined
): string | null => {
  if (lastManuallyRefreshedAt === undefined) {
    return null;
  }

  return lastManuallyRefreshedAt
    ? `Last refreshed at ${datetimeFormatter(new Date(lastManuallyRefreshedAt))}`
    : "Never manually refreshed";
};

export const getLatestLastManuallyRefreshedAt = (
  serverTimestamp: string | null | undefined,
  localTimestamp: string | null
): string | null | undefined => {
  if (!localTimestamp) {
    return serverTimestamp;
  }

  if (!serverTimestamp) {
    return localTimestamp;
  }

  return new Date(serverTimestamp) >= new Date(localTimestamp)
    ? serverTimestamp
    : localTimestamp;
};

export const getDashboardRefreshAvailableAt = (
  lastManuallyRefreshedAt: string | null | undefined,
  nowMilliseconds: number
): Date | null => {
  if (!lastManuallyRefreshedAt) {
    return null;
  }

  const availableAtMilliseconds =
    Math.ceil(
      (new Date(lastManuallyRefreshedAt).getTime() +
        DASHBOARD_REFRESH_COOLDOWN_MS) /
        MILLISECONDS_PER_MINUTE
    ) * MILLISECONDS_PER_MINUTE;

  return availableAtMilliseconds > nowMilliseconds
    ? new Date(availableAtMilliseconds)
    : null;
};

export const getDashboardRefreshAvailableAtText = (
  refreshAvailableAt: Date
): string =>
  `You can manually refresh this dashboard at ${datetimeFormatter(refreshAvailableAt)}`;

export const getLastManuallyRefreshedAtFromRetryAfter = (
  retryAfterSeconds: number | undefined,
  nowMilliseconds: number
): string =>
  new Date(
    nowMilliseconds +
      (retryAfterSeconds ?? DASHBOARD_REFRESH_COOLDOWN_MS / 1000) * 1000 -
      DASHBOARD_REFRESH_COOLDOWN_MS
  ).toISOString();
